/**
 * The web's side of the consents (#399; `docs/superpowers/specs/2026-10-09-consent-screens-decisions.md`).
 *
 * **Reading** is the `me` query's job: `GET /api/me` carries both consents (`MeOut.consents`), so
 * there is no query of its own here and nothing to keep in step with `me`. `useConsentState` is the
 * one place a component asks for one of them.
 *
 * **Answering** is `useAnswerConsent`: one POST that records a grant or a decline for the version of
 * the text the person was shown, and puts the server's answer into the `['me']` query data. That is
 * the whole of the cache work (no invalidation, no refetch): the answer IS the new state, and
 * everything that reads `me` (the gate, a Settings row) draws from it on the next render.
 *
 * **Withdrawing** is `useWithdrawConsent`: one POST to `/api/me/consents/{kind}/withdraw`, which names
 * no version (the record carries the one it withdraws) and answers the new state, put into the `['me']`
 * data the same way. That is the whole of what a withdrawal changes in the page (CS-D10): the Privacy
 * rows, the coach's composer, the dock and the dashboard's analysis all read `me`. Nothing else is
 * invalidated, and nothing is deleted: what is stored stays. **One exception, Health when Android's text stood**
 * (FR2-D12 (b)): the withdrawal ends every Health text held but answers with the web's text alone, so the page
 * ends Android's in the same write and reads `me` once more for the instant (`androidEndedBy`).
 *
 * **Health has two texts** (`HealthText`): `useHealthStates` reads both, `healthRows` says which get a row. The web
 * never answers either; it only withdraws.
 *
 * **Analytics** (#709) is the third kind and the one whose answer the browser also holds, in a cookie
 * (`analytics/consent.ts`). The two answers are joined HERE, in the same tick the server's answer is put into
 * `['me']`: `answerAnalytics` after a grant or a decline, `withdrawAnalytics` after a withdrawal. Until the cookie
 * follows, a yes stored over this browser's no would read as a conflict (the reconciliation, analytics spec *The
 * consent*) and flash the conflict ask; a screen cannot forget it, because it is not the screen's to call.
 *
 * **Off** is `useAiOff`: whether the screens that need the AI consent must draw their "without AI
 * processing" state (CS-D11, CS-D12, CS-D14). It is read from the same `me`.
 *
 * The client is always `"web"` (the server refuses a client it does not know, 422), and the version
 * is whatever the caller passes, which is the deck's: `CONSENT_TEXT_VERSION` in `copy/firstRun.ts`.
 * A hook that picked the version itself would hide the one rule that matters here, that a person is
 * recorded as having answered the text they were SHOWN.
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { answerAnalytics, track, withdrawAnalytics } from '../analytics'
import { apiFetch } from './client'
import type { components } from './generated/schema'
import { useMe } from './hooks'

export type ConsentKind = 'health_data' | 'ai_processing' | 'analytics'
export type ConsentState = components['schemas']['ConsentStateOut']
export type ConsentAnswer = 'grant' | 'decline'

type MeOut = components['schemas']['MeOut']

/** The client every answer from this app names. */
export const CONSENT_CLIENT = 'web'

/**
 * One consent as `GET /api/me` reports it, or `undefined` until `me` has answered. A `me` with no
 * `consents` (an answer from before the consents existed, or a test's hand-built one) reads as
 * `undefined` too: a gate that cannot tell does not ask.
 */
export function useConsentState(kind: ConsentKind): ConsentState | undefined {
  const { data } = useMe()
  return data?.consents?.[kind]
}

/**
 * The two Health texts (AL-D7, FR2-D12 (b): one answer per Health platform). `ios` is the text iOS and the web
 * show, which names Apple Health, and `GET /api/me` reports it as `consents.health_data`; `android` is Android's own,
 * which names Health Connect, reported beside it as `consents.health_data_android`. Each is answered on its own, on its
 * phone. Named by the client that shows the text, as the server names it (`text_for` in `api/platform/consent.py`).
 */
export type HealthText = 'ios' | 'android'

/** The order Health rows are drawn in, and the words of a dialog that speaks of both. */
export const HEALTH_TEXTS: readonly HealthText[] = ['ios', 'android']

export type HealthStates = Record<HealthText, ConsentState | undefined>

/**
 * Where each Health text stands, as `GET /api/me` reports it. `android` is `undefined` from a server that
 * predates the member (it sends none, or null): the web then behaves as it did when there was one Health text.
 */
export function useHealthStates(): HealthStates {
  const { data } = useMe()
  return {
    ios: data?.consents?.health_data,
    android: data?.consents?.health_data_android ?? undefined,
  }
}

/**
 * The Health texts the Privacy tab draws a row for: each that the account has an answer to (agreed, declined or
 * withdrawn), the iOS text first. When neither has one, the iOS text's row alone, "Not asked yet", as before Android
 * had a text: a row for the second text that nobody has asked about would be a row about nothing. Nothing when `me`
 * carries no Health consent at all.
 */
export function healthRows(states: HealthStates): { text: HealthText; state: ConsentState }[] {
  const answered = HEALTH_TEXTS.flatMap((text) => {
    const state = states[text]
    return state && state.status !== 'unasked' ? [{ text, state }] : []
  })
  if (answered.length > 0) return answered
  return states.ios ? [{ text: 'ios' as const, state: states.ios }] : []
}

/** The Health texts that stand: the ones one withdrawal ends. */
export function heldHealthTexts(states: HealthStates): HealthText[] {
  return HEALTH_TEXTS.filter((text) => states[text]?.status === 'granted')
}

/**
 * Puts the server's answer into the `['me']` data, and nothing else. The answer IS the new state, so
 * there is no invalidation and no refetch: everything that reads `me` draws from it on the next render.
 *
 * A `me` read that left before this answer was stored (`useMe` asks again on every mount and on
 * focus) would arrive after it and put the old state back: the gate would ask again, or a withdrawn
 * consent would read as agreed. Dropping it first leaves the answer as the last word.
 */
async function storeConsentState(
  queryClient: QueryClient,
  kind: ConsentKind,
  state: ConsentState,
  /** Runs in the very tick the state is stored: the cookie follows the account (analytics). */
  alsoNow?: () => void,
  /** Added to the consents in the same write, for what the answer ended without reporting. */
  alsoEnded?: (consents: MeOut['consents']) => Partial<MeOut['consents']>,
) {
  await queryClient.cancelQueries({ queryKey: ['me'] })
  queryClient.setQueryData<MeOut>(['me'], (me) =>
    me ? { ...me, consents: { ...me.consents, [kind]: state, ...alsoEnded?.(me.consents) } } : me,
  )
  alsoNow?.()
}

/**
 * What a Health withdrawal ends beyond the state it answers with (FR2-D12 (b)). A withdrawal, wherever it is made,
 * ends every Health text held, but the server answers with the state of the text the web shows (iOS's) alone. If
 * Android's text stood, it reads `withdrawn` from now on, as the server's next read would say, with the version of
 * its own grant. The instant is the answer's when the answer is a withdrawal (one row ends both); an account that
 * held only Android's text gets an unchanged state back, with no instant, and this browser's clock stands in until
 * the read that follows the write (`useWithdrawConsent`) replaces it with the server's.
 */
function androidEndedBy(answered: ConsentState) {
  return (consents: MeOut['consents']): Partial<MeOut['consents']> => {
    const android = consents.health_data_android
    if (android?.status !== 'granted') return {}
    return {
      health_data_android: {
        ...android,
        status: 'withdrawn',
        outdated: false,
        version: null,
        since: null,
        client: null,
        withdrawn_at: answered.withdrawn_at ?? new Date().toISOString(),
        withdrawn_version: android.version ?? null,
      },
    }
  }
}

/**
 * Analytics (#708): a recorded answer to the AI consent is reported as `consent_changed`, with the consent and the
 * answer, once the server has recorded it. The module sends it only while analytics is agreed (a no or a withdrawal of
 * analytics is never reported, and is not this file's: the analytics consent reports its own grant,
 * `answerAnalytics('g')`). The Health consent is agreed on the phone and never answered on the web.
 */
function reportAiConsent(kind: ConsentKind, state: 'granted' | 'declined' | 'withdrawn'): void {
  if (kind === 'ai_processing') track('consent_changed', { consent: 'ai_processing', state })
}

interface AnswerArgs {
  kind: ConsentKind
  answer: ConsentAnswer
  /** The version of the text the person was shown: the deck's, never the server's. */
  version: number
}

/**
 * Records a grant or a decline. Resolves with the new state; rejects with the `ApiError`
 * (`writeErrorLine` words it: the busy sentence for a busy server, the caller's own line otherwise).
 *
 * A write is never sent again on its own (`api/busyRetry.ts`), so a failure here is the person's to
 * try again, and the screen that calls this keeps its buttons until the answer is stored.
 */
export function useAnswerConsent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ kind, answer, version }: AnswerArgs) =>
      apiFetch<ConsentState>(`/api/me/consents/${kind}/${answer}`, {
        method: 'POST',
        body: JSON.stringify({ version, client: CONSENT_CLIENT }),
      }),
    onSuccess: async (state, { kind, answer }) => {
      await storeConsentState(
        queryClient,
        kind,
        state,
        kind === 'analytics' ? () => void answerAnalytics(answer === 'grant' ? 'g' : 'd') : undefined,
      )
      reportAiConsent(kind, answer === 'grant' ? 'granted' : 'declined')
    },
  })
}

/**
 * Withdraws a consent that stands. Resolves with the new state (`withdrawn`, with the version it
 * withdraws); rejects with the `ApiError` and changes nothing on screen: the cache is written only
 * from the server's answer, so a failed withdrawal leaves the row as it was and the dialog says so
 * (`writeErrorLine`). Withdrawing what is not held is the server's no-op and answers the state as it
 * is.
 *
 * Takes effect at once in the page, and on the server from the next request.
 */
export function useWithdrawConsent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ kind }: { kind: ConsentKind }) =>
      apiFetch<ConsentState>(`/api/me/consents/${kind}/withdraw`, {
        method: 'POST',
        body: JSON.stringify({ client: CONSENT_CLIENT }),
      }),
    onSuccess: async (state, { kind }) => {
      // Health (FR2-D12 (b)): the answer is the web's text's state, and the withdrawal also ended Android's text if it
      // stood, which the answer does not say. The page changes from what the answer lets it know, in the same write; one
      // read of `me` follows to replace what the browser had to supply (the instant), and only when something was.
      const androidStood =
        kind === 'health_data' &&
        queryClient.getQueryData<MeOut>(['me'])?.consents?.health_data_android?.status === 'granted'
      // For analytics the page reloads here (the cookie becomes `d`, Google's cookies go): `withdrawAnalytics` is last.
      await storeConsentState(
        queryClient,
        kind,
        state,
        kind === 'analytics' ? () => void withdrawAnalytics() : undefined,
        androidStood ? androidEndedBy(state) : undefined,
      )
      if (androidStood) void queryClient.invalidateQueries({ queryKey: ['me'] })
      reportAiConsent(kind, 'withdrawn')
    },
  })
}

/**
 * True when the screens that need the AI consent must draw their "without AI processing" state
 * (CS-D11, CS-D12, CS-D14): the consent is anything but a standing grant (never asked, declined,
 * withdrawn). The coach's composer shuts, the shortcuts to it go, and no request the server would refuse
 * is sent.
 *
 * - An outdated grant still holds on the server (it is refused only once withdrawn), so it is not "off"
 *   here either, and nothing asks again (CO-D1).
 * - **False while `me` has not answered, or carries no consents** (an answer from before they existed, or
 *   a test's hand-built one). A screen that cannot tell behaves as it did before the consents: nothing is
 *   hidden and nothing is refused on a guess. In the app that window does not reach a routed page:
 *   `RequireFirstRun` draws none until `/api/me` has answered.
 */
export function useAiOff(): boolean {
  const state = useConsentState('ai_processing')
  return state !== undefined && state.status !== 'granted'
}
